import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Send, FileText, BookmarkPlus } from "lucide-react";
import { extraLine } from "./AdminExtras";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0";
export const money = (v) => { const n = Number(v || 0); return `${n < 0 ? "-" : ""}£${Math.abs(n).toFixed(2)}`; };

const QUOTE_BADGE = { sent: ["Quote sent · awaiting customer", "text-amber-300"], accepted: ["Quote accepted", "text-emerald-300"], declined: ["Quote declined by customer", "text-brand"], superseded: ["Confirmed without quote acceptance", "text-white/60"], invoiced: ["Invoice sent", "text-sky-300"] };

export const QuoteSummary = ({ q, token, onChanged }) => {
  const [t, cls] = QUOTE_BADGE[q.status] || [q.status, "text-white/60"];
  const bal = q.balance_paid ? 0 : q.balance;
  const markPaid = async () => {
    if (!window.confirm(`Mark ${money(q.balance)} as paid (cash, card machine or bank transfer)?`)) return;
    try { await axios.post(`${API}/admin/quotes/${q.token}/mark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Marked as paid"); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't mark as paid"); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm" data-testid="booking-quote-summary">
      <span className={`font-semibold ${cls}`}>{t}</span>
      <span className="text-white/70">Total {money(q.total)}</span>
      {q.balance_paid ? <span className="text-emerald-300">{q.paid_by_hand ? "Paid (marked by you)" : "Balance paid online"}</span>
        : <span className="text-white/55">{bal < 0 ? `Refund ${money(-bal)}${q.refund_amount ? " (done)" : q.refund_failed ? " (failed, refund manually)" : ""}` : `Balance ${money(bal)}`}</span>}
      {q.booking && <span className="text-emerald-300" data-testid="quote-booked">Booked {q.booking.reference} · {q.booking.date} {q.booking.time}</span>}
      {q.arrange_requested && !q.booking && <span className="font-semibold text-amber-300" data-testid="quote-arrange">Customer asked you to arrange a date</span>}
      {q.duplicate_sessions?.length > 0 && <span className="font-semibold text-brand" data-testid="booking-quote-duplicate">Balance paid twice, refund one payment in Stripe</span>}
      {!q.balance_paid && bal > 0 && ["accepted", "invoiced"].includes(q.status) && <button onClick={markPaid} className="ml-auto rounded-md border border-white/15 px-2.5 py-1 text-xs font-semibold text-white/80 hover:border-emerald-400 hover:text-emerald-300" data-testid="quote-mark-paid">Mark paid</button>}
    </div>
  );
};

export const paidSoFar = (b) => (b.payment_status !== "paid" ? 0 : Number((b.payment_method === "online" ? b.online_amount : b.deposit_amount) || 0));

const startItems = (b, prices) => (b.quote?.items?.length ? b.quote.items.map((i) => ({ ...i }))
  : [{ description: b.service && b.service !== "Not sure yet" ? b.service : "", amount: b.online_amount || prices[b.service] || "" }]);

const Line = ({ item, i, onChange, onRemove, canRemove, onSave }) => (
  <div className="flex gap-2" data-testid={`quote-line-${i}`}>
    <Input value={item.description} maxLength={120} placeholder="e.g. Pops & bangs, or Discount" onChange={(e) => onChange({ ...item, description: e.target.value })} className={`${field} h-10 flex-1`} data-testid={`quote-line-desc-${i}`} />
    <div className="relative w-32">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/50">£</span>
      <Input type="number" step="0.01" value={item.amount} onChange={(e) => onChange({ ...item, amount: e.target.value })} className={`${field} h-10 pl-7`} data-testid={`quote-line-amount-${i}`} />
    </div>
    {onSave ? <button type="button" title="Save as extra" onClick={onSave} className="w-6 text-white/40 hover:text-brand" data-testid={`quote-line-save-${i}`}><BookmarkPlus className="mx-auto h-4 w-4" /></button> : <span className="w-6" />}
    <button type="button" disabled={!canRemove} onClick={onRemove} className="px-2 text-white/40 hover:text-brand disabled:opacity-30" data-testid={`quote-line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

const Totals = ({ total, paid }) => {
  const bal = total - paid;
  return (
    <div className="space-y-1 rounded-lg border border-white/10 bg-[#0b0c0f] p-4 text-sm" data-testid="quote-totals">
      <div className="flex justify-between font-semibold text-white"><span>Total</span><span data-testid="quote-total">{money(total)}</span></div>
      {paid > 0 && <div className="flex justify-between text-white/55"><span>Already paid</span><span>{money(-paid)}</span></div>}
      <div className="flex justify-between font-semibold text-brand"><span>{bal < 0 ? "Refund to customer on accept" : "Balance to pay"}</span><span data-testid="quote-balance">{money(Math.abs(bal))}</span></div>
    </div>
  );
};

const DocType = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-1 rounded-lg border border-white/10 p-1" data-testid="quote-doc-type">
    {[["quote", "Quote", "customer accepts it"], ["invoice", "Invoice", "customer just pays"]].map(([k, l, d]) => (
      <button type="button" key={k} onClick={() => onChange(k)} className={`rounded-md px-3 py-2 text-left text-sm transition-colors ${value === k ? "bg-brand text-white" : "text-white/60 hover:text-white"}`} data-testid={`quote-doc-${k}`}>
        <span className="block font-semibold">{l}</span><span className="block text-[11px] opacity-75">{d}</span>
      </button>
    ))}
  </div>
);

const QuoteDialog = ({ booking, token, onClose, onSent, kind = "booking" }) => {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [pdf, setPdf] = useState(true);
  const [docType, setDocType] = useState("quote");
  const touched = useRef(false);
  const editItems = (v) => { touched.current = true; setItems(v); };
  const [sending, setSending] = useState(false);
  const [extras, setExtras] = useState([]);
  const loadExtras = () => axios.get(`${API}/admin/extras`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setExtras(data)).catch(() => {});
  useEffect(() => { if (booking) loadExtras(); }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!booking) return;
    setNote(booking.quote?.note || "");
    setPdf(booking.quote ? booking.quote.include_pdf : true);
    setDocType(booking.quote?.doc_type || (booking.booking_status === "confirmed" ? "invoice" : "quote"));
    touched.current = false;
    setItems(startItems(booking, {}));
    axios.get(`${API}/config`).then(({ data }) => { if (!booking.quote && !touched.current) setItems(startItems(booking, data.online_prices || {})); }).catch(() => {});
  }, [booking]);
  if (!booking) return null;
  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const paid = kind === "question" ? 0 : paidSoFar(booking);
  const word = docType === "invoice" ? "invoice" : "quote";
  const valid = items.length > 0 && items.every((i) => i.description.trim() && i.amount !== "" && !isNaN(Number(i.amount))) && total >= 0;
  const known = new Set([booking.service || "", ...extras.flatMap((x) => [x.name, extraLine(x).description])].map((t) => t.toLowerCase()));
  const isKnown = (d) => { const t = d.trim().toLowerCase(); return known.has(t) || known.has(t.slice(0, 60)); };
  const saveExtra = async (it) => {
    try {
      await axios.post(`${API}/admin/extras`, { name: it.description.trim().slice(0, 60), price: Number(it.amount), note: "" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${it.description.trim()}" saved to your extras`);
      loadExtras();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save extra");
    }
  };
  const send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/${kind === "question" ? "questions" : "enquiries"}/${booking.id}/quote`, { items: items.map((i) => ({ description: i.description, amount: Number(i.amount) })), note, include_pdf: pdf, doc_type: docType, origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${docType === "invoice" ? "Invoice" : "Quote"} sent to ${booking.email}`);
      onSent();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check every line has a description and amount.");
    } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#111217] text-white sm:max-w-xl" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send {word} · {booking.reference}</DialogTitle>
          <DialogDescription className="text-white/55">{docType === "invoice" ? `${booking.name} gets an invoice to pay online or by bank transfer.${kind === "booking" && booking.booking_status === "pending" ? " Paying it confirms the booking." : ""}` : kind === "question" ? `${booking.name} accepts it, then picks a date or asks you to arrange one.` : booking.booking_status === "pending" ? `${booking.name} must accept it to confirm the booking.` : `${booking.name} is asked to accept the new price.`} Use a minus amount for discounts.</DialogDescription>
        </DialogHeader>
        <DocType value={docType} onChange={setDocType} />
        <div className="space-y-2">
          {items.map((it, i) => (
            <Line key={i} i={i} item={it} canRemove={items.length > 1} onChange={(v) => editItems(items.map((x, j) => (j === i ? v : x)))} onRemove={() => editItems(items.filter((_, j) => j !== i))}
              onSave={it.description.trim() && it.amount !== "" && !isKnown(it.description) ? () => saveExtra(it) : null} />
          ))}
          <button type="button" disabled={items.length >= 15} onClick={() => editItems([...items, { description: "", amount: "" }])} className="flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-white disabled:opacity-40" data-testid="quote-add-line"><Plus className="h-4 w-4" />Add line</button>
        </div>
        {extras.length > 0 && (
          <div data-testid="quote-extras">
            <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-white/50">Tap to add an extra</div>
            <div className="flex flex-wrap gap-2">
              {extras.map((x) => (
                <button type="button" key={x.id} disabled={items.length >= 15} onClick={() => editItems([...items.filter((i) => i.description.trim() || i.amount !== ""), extraLine(x)])} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-white/80 transition-colors hover:border-brand hover:text-white disabled:opacity-40" data-testid={`quote-extra-${x.id}`}>
                  <Plus className="-mt-0.5 mr-1 inline h-3.5 w-3.5 text-brand" />{x.name} <span className="text-white/45">{money(x.price)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <Totals total={total} paid={paid} />
        <Textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for the customer, e.g. includes before & after dyno run" className={`${field} min-h-[70px]`} data-testid="quote-note" />
        <label className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3 text-sm">
          <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-brand" />Include a PDF {word} download</span>
          <Switch checked={pdf} onCheckedChange={setPdf} data-testid="quote-pdf-toggle" />
        </label>
        <button disabled={!valid || sending} onClick={send} className="btn-red flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold disabled:opacity-50" data-testid="quote-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{`Send ${word} to customer`}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
